// src/contexts/AuthContext.tsx
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { useQuery, useQueryClient, QueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabaseClient';

// Updated Profile interface
export interface Profile {
  user_id: string;
  full_name: string | null;
  email: string;
  zapier_api_key?: string | null;
  created_at_profile?: string; // From 'profiles' table's 'created_at' column
  user_type?: string | null;
  partner_id?: string | null;
}

// Updated AuthContextType
export interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean; // This is the crucial loading state for consumers like ProtectedRoute
  isFoundingMember: boolean;
  signOut: () => Promise<{ error: Error | null }>;
  queryClient: QueryClient;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const FOUNDING_MEMBER_CUTOFF_DATE = new Date('2025-06-01T00:00:00.000Z');

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [overallLoading, setOverallLoading] = useState(true); // Master loading state for auth readiness
  const [initialAuthCheckCompleted, setInitialAuthCheckCompleted] = useState(false); // Tracks if getSession() has run
  const [isFoundingMember, setIsFoundingMember] = useState<boolean>(false);
  const queryClient = useQueryClient();

  const { data: profile, isLoading: profileLoading, error: profileError } = useQuery<Profile | null>({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      // console.log("Fetching profile for user:", user.id);
      try {
        const { data, error, status } = await supabase
          .from('profiles')
          .select('user_id, full_name, email, zapier_api_key, created_at, user_type, partner_id') // Explicitly select columns
          .eq('user_id', user.id)
          .single();

        if (error && status !== 406) { // 406: "Not Acceptable", Supabase returns this if .single() finds 0 or >1 rows
          console.error("Error fetching profile:", error);
          throw error;
        }
        if (!data) {
            console.log("No profile data found for user:", user.id);
            return null;
        }
        
        // Cast to ensure all Profile fields are present or undefined
        const fetchedProfileData = data as {
            user_id: string;
            full_name: string | null;
            email: string | null; // email from profiles can be null
            zapier_api_key?: string | null;
            created_at?: string; // created_at from profiles table
            user_type?: string | null;
            partner_id?: string | null;
        };

        return {
          user_id: fetchedProfileData.user_id || user.id,
          full_name: fetchedProfileData.full_name || null,
          email: fetchedProfileData.email || user.email || 'Error: Email not found',
          zapier_api_key: fetchedProfileData.zapier_api_key || null,
          created_at_profile: fetchedProfileData.created_at,
          user_type: fetchedProfileData.user_type ?? null,
          partner_id: fetchedProfileData.partner_id ?? null,
        };

      } catch (err) {
        console.error("Profile query function error:", err);
        return null;
      }
    },
    enabled: !!user && initialAuthCheckCompleted, // Enable only if user exists AND initial auth check is done
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  useEffect(() => {
    // This effect runs once on mount to check initial session and set up listener.
    // setOverallLoading(true); // Default state is true

    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      // console.log("Initial session from getSession():", initialSession);
      setSession(initialSession);
      const initialUser = initialSession?.user ?? null;
      setUser(initialUser);

      if (initialUser?.created_at) {
        setIsFoundingMember(new Date(initialUser.created_at) < FOUNDING_MEMBER_CUTOFF_DATE);
      } else {
        setIsFoundingMember(false);
      }
    }).catch(error => {
      console.error("Error getting initial session:", error);
      setSession(null);
      setUser(null);
      setIsFoundingMember(false);
    }).finally(() => {
      // Mark that the initial attempt to get the session has finished.
      setInitialAuthCheckCompleted(true);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, changedSession) => {
        // console.log("Auth state changed:", _event, "New session:", changedSession);
        setSession(changedSession);
        const currentUser = changedSession?.user ?? null;
        setUser(currentUser);

        if (currentUser?.created_at) {
          setIsFoundingMember(new Date(currentUser.created_at) < FOUNDING_MEMBER_CUTOFF_DATE);
        } else {
          setIsFoundingMember(false);
        }

        // If user signs out, ensure profile query data is cleared by react-query due to 'enabled' becoming false.
        if (_event === 'SIGNED_OUT') {
            console.log("User signed out (detected by listener). Profile query will be disabled.");
            // No need to manually setProfile(null) as react-query handles data based on 'enabled'
        }
        
        // Ensure initialAuthCheckCompleted is true if onAuthStateChange fires first (e.g., during OAuth redirect)
        if (!initialAuthCheckCompleted) {
            setInitialAuthCheckCompleted(true);
        }
      }
    );

    return () => {
      // console.log("Unsubscribing from auth state changes.");
      subscription.unsubscribe();
    };
  }, []); // Empty dependency array: run only once on mount.

  // This effect determines the `overallLoading` state for the context.
  useEffect(() => {
    if (!initialAuthCheckCompleted) {
      // If Supabase `getSession()` hasn't completed its attempt, we are definitely loading.
      setOverallLoading(true);
    } else {
      // `getSession()` has completed.
      if (user) {
        // If there's a user, we are loading until their profile is also loaded (or failed/no profile).
        // profileLoading will be false if query is disabled (no profile) or completes or errors.
        setOverallLoading(profileLoading);
      } else {
        // No user (either never logged in, or logged out). Auth is resolved. Not loading.
        setOverallLoading(false);
      }
    }
  }, [initialAuthCheckCompleted, user, profileLoading]);

  // Log profile fetch errors if any (optional, for debugging)
  useEffect(() => {
    if (profileError) {
      console.error("AuthContext: Profile fetching encountered an error:", profileError.message);
      // Potentially handle profile fetch errors here, e.g., show a toast
    }
  }, [profileError]);


  const signOut = async (): Promise<{ error: Error | null }> => {
    // setOverallLoading(true); // Let onAuthStateChange and subsequent useEffects handle loading state
    try {
      console.log("Signing out via context function...");
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Error signing out (supabase):', error);
        return { error };
      }
      // Auth listener (onAuthStateChange) will handle setting user and session to null.
      // Profile query will become disabled and its data will be managed by React Query.
      queryClient.clear(); // Clear all react-query cache on sign out
      console.log("Supabase signOut successful, react-query cache cleared.");
      return { error: null };
    } catch (error: any) {
      console.error('Sign out function exception:', error);
      return { error: error instanceof Error ? error : new Error('Unknown sign out error') };
    }
    // No finally setLoading(false) here; rely on state propagation from auth listener.
  };

  const value: AuthContextType = {
    session,
    user,
    profile: profile ?? null, // Provide profile or null
    loading: overallLoading, // This is the corrected loading state
    isFoundingMember,
    signOut,
    queryClient,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}